import { FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { useIsMounted } from '@ps/web-shared-components';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { select } from '@swf/core/store';
import type { OneTrust } from '@swf/core/types/Window';

declare global {
  interface Window {
    oneTrust: OneTrust;
  }
}

const useOneTrust = () => {
  const oneTrustEnabled = useFeature(FeatureFlag.EnableOneTrust);
  const mobileEmbedded = useSelector(select.device.isMobileEmbedded);
  const [oneTrust, setOneTrust] = useState<OneTrust>();
  const [alertClosed, setAlertClosed] = useState(false);
  const isMounted = useIsMounted();

  useEffect(() => {
    const checkOneTrust = () => isMounted() && setOneTrust(window.OneTrust);
    if (oneTrustEnabled && !alertClosed) {
      if (mobileEmbedded) {
        if (!window.OneTrust) {
          window.OneTrust = new Proxy(
            {},
            {
              set(target, key, value) {
                (target as Record<string | symbol, unknown>)[key] = value;
                checkOneTrust();
                return true;
              }
            }
          ) as OneTrust;
        } else {
          checkOneTrust();
        }
      } else {
        checkOneTrust();
        document.addEventListener('onetrustloaded', checkOneTrust);
      }
    }

    return () => {
      if (oneTrustEnabled && !mobileEmbedded) {
        document.removeEventListener('onetrustloaded', checkOneTrust);
      }
    };
  }, [alertClosed, mobileEmbedded, isMounted, oneTrustEnabled]);

  useEffect(() => {
    if (oneTrust && !alertClosed) {
      setAlertClosed(oneTrust.IsAlertBoxClosed?.());
      oneTrust.OnConsentChanged?.(
        () => isMounted() && setAlertClosed(oneTrust.IsAlertBoxClosed?.())
      );
    }
  }, [alertClosed, isMounted, oneTrust]);

  return { alertClosed, oneTrust, oneTrustEnabled };
};

export default useOneTrust;
